import { useState } from 'react';
import { Plus, Save, Trash2, Check, X, Loader2, PlaySquare } from 'lucide-react';
import { apiService } from '@/services/api';
import { useI18n } from '@/contexts/i18n-hook';
import { TabelaRiscos } from '@/components/apreciacao/tabela-riscos';

interface RiscoTemplate {
    id?: string;
    numero: number;
    parte: string;
    tipoPerigo: string;
    categoria: string;
    hrnAtual: number;
    classificacaoRisco?: string;
    plr?: string;
    sil?: string;
    categoriaSeguranca?: string;
    // Outros campos relevantes para risco podem ser adicionados aqui
}

interface ParteTemplate {
    nome: string;
    riscos: RiscoTemplate[];
}

interface EditorPartesTemplateProps {
    tipoId: string;
    partesIniciais: ParteTemplate[];
    onSalvar: (partes: ParteTemplate[]) => void;
}

export function EditorPartesTemplate({ tipoId, partesIniciais, onSalvar }: EditorPartesTemplateProps) {
    const { t } = useI18n();
    const [partes, setPartes] = useState<ParteTemplate[]>(partesIniciais || []);
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);

    // Estados para nova parte
    const [modoCriarParte, setModoCriarParte] = useState(false);
    const [novaParteNome, setNovaParteNome] = useState('');

    // Adicionar uma nova parte vazia
    const handleAdicionarParte = () => {
        if (!novaParteNome.trim()) return;
        const novaParte: ParteTemplate = {
            nome: novaParteNome.trim(),
            riscos: []
        };
        setPartes([...partes, novaParte]);
        setNovaParteNome('');
        setModoCriarParte(false);
    };

    // Remover uma parte inteira
    const handleRemoverParte = (indexParte: number) => {
        if (!confirm(t('templates.editorPartes.confirmarRemoverParte'))) return;
        const novasPartes = [...partes];
        novasPartes.splice(indexParte, 1);
        setPartes(novasPartes);
    };

    // Renomear uma parte
    const handleRenomearParte = (indexParte: number, novoNome: string) => {
        const novasPartes = [...partes];
        novasPartes[indexParte].nome = novoNome;
        setPartes(novasPartes);
    };

    // Funções para manipular riscos dentro de uma parte
    const handleAdicionarRisco = (indexParte: number) => {
        const novasPartes = [...partes];
        const riscosDaParte = novasPartes[indexParte].riscos;
        const novoRisco: RiscoTemplate = {
            id: `temp-${Date.now()}`,
            numero: riscosDaParte.length + 1,
            parte: novasPartes[indexParte].nome,
            tipoPerigo: 'Mecânico',
            categoria: 'Corte',
            hrnAtual: 5, // Valor padrão de exemplo
            classificacaoRisco: 'Baixo'
        };
        novasPartes[indexParte].riscos.push(novoRisco);
        setPartes(novasPartes);
    };

    const handleRemoverRisco = (indexParte: number, riscoIdOuNumero: string) => {
        const novasPartes = [...partes];
        novasPartes[indexParte].riscos = novasPartes[indexParte].riscos.filter(
            r => r.id !== riscoIdOuNumero && r.numero.toString() !== riscoIdOuNumero
        );
        // Reordenar números
        novasPartes[indexParte].riscos.forEach((r, idx) => r.numero = idx + 1);
        setPartes(novasPartes);
    };

    const handleEditarRisco = (indexParte: number, riscoEditado: RiscoTemplate) => {
        // Para simplificar a POC, aqui abriria um modal de edição de risco.
        // Como estamos apenas visualizando a estrutura, vamos simular uma edição rápida via prompt.
        const novoTipo = prompt(t('templates.editorPartes.promptTipoPerigo'), riscoEditado.tipoPerigo) || riscoEditado.tipoPerigo;
        const novaCat = prompt(t('templates.editorPartes.promptCategoria'), riscoEditado.categoria) || riscoEditado.categoria;
        const novoHRNStr = prompt(t('templates.editorPartes.promptHrn'), riscoEditado.hrnAtual.toString());
        const novoHRN = novoHRNStr ? parseFloat(novoHRNStr) : riscoEditado.hrnAtual;

        const novasPartes = [...partes];
        const idxRisco = novasPartes[indexParte].riscos.findIndex(r => r.id === riscoEditado.id || r.numero === riscoEditado.numero);

        if (idxRisco >= 0) {
            novasPartes[indexParte].riscos[idxRisco] = {
                ...riscoEditado,
                tipoPerigo: novoTipo,
                categoria: novaCat,
                hrnAtual: novoHRN
            };
            setPartes(novasPartes);
        }
    };

    const handleSalvarTudo = async () => {
        setSalvando(true);
        setErro(null);
        try {
            // O payload esperado pelo backend depende da estrutura do serviço atualizarTemplateMultiParte
            // Em services/template-multiparte.service.ts -> partes é Json[]
            const response = await apiService.atualizarTemplateMultiParte(tipoId, {
                partes: partes
            });

            if (response.success) {
                onSalvar(partes);
            } else {
                setErro(response.message || t('templates.editorPartes.erroSalvarPartes'));
            }
        } catch (err) {
            setErro(t('templates.editorPartes.erroConexaoSalvar'));
        } finally {
            setSalvando(false);
        }
    };

    return (
        <div className="bg-white dark:bg-schmersal-gray-dark border border-gray-200 dark:border-schmersal-gray-darker rounded-xl shadow-sm p-6 transition-colors">
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100 dark:border-gray-800">
                <div className="flex items-center gap-2">
                    <div className="bg-green-100 dark:bg-green-900/30 p-2 rounded-lg text-schmersal-green dark:text-green-400">
                        <PlaySquare className="h-5 w-5" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">{t('templates.partes.titulo')}</h2>
                </div>

                <button
                    onClick={handleSalvarTudo}
                    disabled={salvando}
                    className="px-4 py-2 bg-schmersal-blue hover:bg-blue-700 text-white rounded-md text-sm font-medium shadow-sm transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                    {salvando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {t('templates.editorPartes.salvarTudo')}
                </button>
            </div>

            {erro && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-md border border-red-200 dark:border-red-800 text-sm">
                    {erro}
                </div>
            )}

            {partes.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
                    <p className="text-gray-500 dark:text-gray-400 mb-4">{t('templates.editorPartes.vazio')}</p>
                </div>
            ) : (
                <div className="space-y-8">
                    {partes.map((parte, index) => (
                        <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-800/30">
                            <div className="flex items-center justify-between p-4 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                                <div className="flex items-center gap-2 flex-1">
                                    <span className="font-semibold text-gray-700 dark:text-gray-200">{t('templates.editorPartes.parteNumero', { numero: index + 1 })}</span>
                                    <input
                                        type="text"
                                        value={parte.nome}
                                        onChange={(e) => handleRenomearParte(index, e.target.value)}
                                        className="flex-1 bg-transparent border-none focus:ring-0 text-gray-900 dark:text-white font-medium px-2 py-1"
                                        placeholder={t('templates.editorPartes.parteNomePlaceholder')}
                                    />
                                </div>
                                <button
                                    onClick={() => handleRemoverParte(index)}
                                    className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                                    title={t('templates.editorPartes.removerParte')}
                                >
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </div>

                            <div className="p-4">
                                <TabelaRiscos
                                    riscos={parte.riscos}
                                    modoEdicao={true}
                                    onAdicionar={() => handleAdicionarRisco(index)}
                                    onRemover={(riscoId) => handleRemoverRisco(index, riscoId)}
                                    onEditar={(risco) => handleEditarRisco(index, risco as RiscoTemplate)}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Adicionar Nova Parte */}
            <div className="mt-6 pt-6 border-t border-gray-100 dark:border-gray-800">
                {modoCriarParte ? (
                    <div className="flex gap-2 max-w-md">
                        <input
                            type="text"
                            autoFocus
                            placeholder={t('templates.editorPartes.novaPartePlaceholder')}
                            value={novaParteNome}
                            onChange={(e) => setNovaParteNome(e.target.value)}
                            className="flex-1 rounded-md border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-white shadow-sm focus:border-schmersal-blue sm:text-sm"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleAdicionarParte();
                                if (e.key === 'Escape') setModoCriarParte(false);
                            }}
                        />
                        <button
                            onClick={handleAdicionarParte}
                            disabled={!novaParteNome.trim()}
                            className="p-2 bg-green-500 hover:bg-green-600 text-white rounded-md disabled:opacity-50 transition-colors"
                            title={t('comum.acao.adicionar')}
                        >
                            <Check className="h-4 w-4" />
                        </button>
                        <button
                            onClick={() => setModoCriarParte(false)}
                            className="p-2 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-md transition-colors"
                            title={t('comum.acao.cancelar')}
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                ) : (
                    <button
                        onClick={() => setModoCriarParte(true)}
                        className="px-4 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-schmersal-blue hover:text-schmersal-blue dark:hover:border-blue-400 dark:hover:text-blue-400 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                    >
                        <Plus className="h-4 w-4" /> {t('templates.editorPartes.adicionarNovaParte')}
                    </button>
                )}
            </div>
        </div>
    );
}
